import { createContext, useCallback, useContext, useMemo } from "react";

import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "http://localhost:8080/api";

const ApiContext = createContext();

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};

export const ApiProvider = ({ children }) => {
  // Generic request handler
  const request = useCallback(async (method, path, data = null, config = {}) => {
    try {
      // Ensure path starts with /
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      const url = `${API}${cleanPath}`;

      const token = localStorage.getItem("token");
      const headers = {
        ...config.headers,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0"
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios({
        method,
        url,
        data,
        ...config,
        headers
      });

      return response;
    } catch (error) {
      console.error(`API Error [${method} ${path}]:`, error);

      // Dispatch global unauthorized event on 401
      if (error.response && error.response.status === 401) {
        window.dispatchEvent(new Event("unauthorized"));
      }

      // Enrich error message with backend detail if available
      const detail = error.response?.data?.detail || error.response?.data?.message;
      if (detail) {
        error.message = detail;
      }

      throw error;
    }
  }, []);

  const api = useMemo(
    () => ({
      get: (path, config) => request("GET", path, null, config),
      post: (path, data, config) => request("POST", path, data, config),
      put: (path, data, config) => request("PUT", path, data, config),
      delete: (path, config) => request("DELETE", path, null, config),
      patch: (path, data, config) => request("PATCH", path, data, config)
    }),
    [request]
  );

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};
